// Organization sign-in via Google Cloud Identity Platform (project "iskcon-ciam").
// Adapted from payreq-app/src/lib/gcip.ts. Difference: this is the LOGIN PAGE, so every
// path here produces a raw GCIP ID token (a string) which App.tsx posts to the local
// login-service at POST /sso/login — there is no app-JWT exchange on this page.
//
// Configure via env:
//   VITE_GCIP_API_KEY     — the Identity Platform web API key (public identifier)
//   VITE_GCIP_AUTH_DOMAIN — e.g. iskcon-ciam.firebaseapp.com
//   VITE_GCIP_PROVIDERS   — comma list of provider ids to offer (default "google.com");
//                           a provider must ALSO be enabled in the GCIP console.

import { initializeApp, type FirebaseApp } from "firebase/app";
import {
  FacebookAuthProvider,
  getAuth,
  GoogleAuthProvider,
  OAuthProvider,
  signInWithEmailAndPassword,
  signInWithPopup,
  type AuthProvider,
} from "firebase/auth";

const API_KEY = import.meta.env.VITE_GCIP_API_KEY as string | undefined;
const AUTH_DOMAIN = import.meta.env.VITE_GCIP_AUTH_DOMAIN as string | undefined;

export const GCIP_CONFIGURED = Boolean(API_KEY && AUTH_DOMAIN);

const PROVIDER_LABELS: Record<string, string> = {
  "google.com": "Google",
  "facebook.com": "Facebook",
  "apple.com": "Apple",
  "microsoft.com": "Microsoft",
};

/** The providers this build offers, in configured order. */
export const GCIP_PROVIDERS: { id: string; label: string }[] = (
  (import.meta.env.VITE_GCIP_PROVIDERS as string | undefined) ?? "google.com"
)
  .split(",")
  .map((s) => s.trim())
  .filter(Boolean)
  .map((id) => ({ id, label: PROVIDER_LABELS[id] ?? id }));

let app: FirebaseApp | null = null;
function auth() {
  if (!app) {
    app = initializeApp({ apiKey: API_KEY, authDomain: AUTH_DOMAIN });
  }
  return getAuth(app);
}

function providerFor(id: string): AuthProvider {
  if (id === "google.com") {
    const p = new GoogleAuthProvider();
    p.setCustomParameters({ prompt: "select_account" });
    return p;
  }
  if (id === "facebook.com") {
    return new FacebookAuthProvider();
  }
  const p = new OAuthProvider(id);
  if (id === "apple.com") {
    p.addScope("email");
    p.addScope("name");
  }
  return p;
}

/** Thrown-away marker for "the user just closed the popup" — callers stay silent on it. */
export class SignInCancelled extends Error {}

/** Sign in with one federated GCIP provider (popup) and return the GCIP ID token. */
export async function signInWithProvider(providerId: string): Promise<string> {
  let cred;
  try {
    cred = await signInWithPopup(auth(), providerFor(providerId));
  } catch (e) {
    const code = (e as { code?: string } | undefined)?.code ?? "";
    if (code === "auth/popup-closed-by-user" || code === "auth/cancelled-popup-request") {
      throw new SignInCancelled();
    }
    if (code === "auth/operation-not-allowed") {
      throw new Error(
        `${PROVIDER_LABELS[providerId] ?? providerId} sign-in is not enabled yet. Contact the administrator.`
      );
    }
    if (code === "auth/account-exists-with-different-credential") {
      throw new Error("This email already signed in with a different provider. Use that provider instead.");
    }
    throw e instanceof Error ? e : new Error("Sign-in failed.");
  }
  return cred.user.getIdToken();
}

/** Sign in with a native GCIP email/password account and return the GCIP ID token. */
export async function signInWithEmailPassword(email: string, password: string): Promise<string> {
  let cred;
  try {
    cred = await signInWithEmailAndPassword(auth(), email, password);
  } catch (e) {
    const code = (e as { code?: string } | undefined)?.code ?? "";
    if (
      code === "auth/invalid-credential" ||
      code === "auth/wrong-password" ||
      code === "auth/user-not-found" ||
      code === "auth/invalid-email"
    ) {
      throw new Error("Incorrect email or password.");
    }
    if (code === "auth/too-many-requests") {
      throw new Error("Too many attempts — please wait a few minutes and try again.");
    }
    if (code === "auth/user-disabled") {
      throw new Error("This account has been disabled. Contact the administrator.");
    }
    if (code === "auth/operation-not-allowed") {
      throw new Error("Email/password sign-in is not enabled yet. Contact the administrator.");
    }
    throw e instanceof Error ? e : new Error("Sign-in failed.");
  }
  return cred.user.getIdToken();
}
